import { numbersApproximatelyEqual } from './tmpCopied/float';
import assertNever from '@watershed/shared-util/src/assertNever';
import { ExprType, stringifyType } from './expr_types';

export type ASTBoolean = Readonly<{
  t: 'boolean';
  v: boolean;
}>;

export type ASTNumber = Readonly<{
  t: 'number';
  v: number;
}>;

export type ASTString = Readonly<{
  t: 'string';
  v: string;
}>;

export type ASTIdentifier = Readonly<{
  t: 'identifier';
  name: string;
}>;

export type ASTUnary = Readonly<{
  t: 'unary';
  op: '-';
  expr: ASTExpr;
}>;

export type ASTBinary = Readonly<{
  t: 'binary';
  op: '+' | '-' | '*' | '/';
  l: ASTExpr;
  r: ASTExpr;
}>;

export type ASTComparison = Readonly<{
  t: 'comparison';
  op: '==' | '!=' | '>=' | '>' | '<=' | '<';
  l: ASTExpr;
  r: ASTExpr;
}>;

export type ASTCall = Readonly<{
  t: 'call';
  name: string;
  args: ReadonlyArray<ASTExpr>;
  kwargs: ReadonlyMap<string, ASTKwarg>;
}>;

export type ASTExactKwarg = Readonly<{
  t: 'exact';
  expr: ASTExpr;
}>;

export type ASTFuzzyKwarg = Readonly<{
  t: 'fuzzy';
  expr: ASTExpr;
}>;

export type ASTKwarg = ASTExactKwarg | ASTFuzzyKwarg;

export type ASTNull = Readonly<{
  t: 'null';
}>;

export type ASTExpr =
  | ASTBoolean
  | ASTNumber
  | ASTUnary
  | ASTString
  | ASTIdentifier
  | ASTBinary
  | ASTComparison
  | ASTCall
  | ASTNull;

export type ASTStmt = Readonly<{
  t: 'assign';
  name: string;
  expr: ASTExpr;

  // Only available if the author explicitly defined a type for the variable.
  // If not, the variable inherits the type from expr.
  typeDeclaration?: ExprType;
}>;

export type ASTPrimitive = ASTBoolean | ASTNumber | ASTString | ASTNull;

export function isAstPrimitive(expr: ASTExpr): boolean {
  return (
    expr.t === 'number' ||
    expr.t === 'boolean' ||
    expr.t === 'string' ||
    expr.t === 'null'
  );
}

export type ASTProgram = ReadonlyArray<ASTStmt>;

export function stringifyExpr(expr: ASTExpr): string {
  switch (expr.t) {
    case 'string':
      return JSON.stringify(expr.v);
    case 'number':
      return expr.v.toString();
    case 'boolean':
      return expr.v ? 'true' : 'false';
    case 'unary':
      return `${expr.op}(${stringifyExpr(expr.expr)})`;
    case 'identifier':
      return expr.name;
    case 'binary':
      return `(${stringifyExpr(expr.l)} ${expr.op} ${stringifyExpr(expr.r)})`;
    case 'comparison':
      return `(${stringifyExpr(expr.l)} ${expr.op} ${stringifyExpr(expr.r)})`;
    case 'call':
      let args = '';
      for (const arg of expr.args) {
        args += stringifyExpr(arg);
        args += ', ';
      }
      for (const [kw, { t, expr: arg }] of expr.kwargs.entries()) {
        args += kw;
        args += t === 'exact' ? '=' : '~';
        args += stringifyExpr(arg);
        args += ', ';
      }
      if (args.length > 0) {
        args = args.substring(0, args.length - 2);
      }
      return `${expr.name}(${args})`;
    case 'null':
      return 'null';
    default:
      assertNever(expr);
  }
}

export function stringifyProgram(prog: ASTProgram): string {
  let out = '';
  for (const assignment of prog) {
    out = stringifyStmt(assignment, out);
    out += '\n';
  }
  return out;
}

export function stringifyStmt(assignment: ASTStmt, out: string = ''): string {
  out += assignment.name;
  if (assignment.typeDeclaration) {
    const ty = assignment.typeDeclaration;
    switch (ty.ty) {
      case 'string':
      case 'boolean':
      case 'number':
      case 'date':
      case 'datetime':
      case 'nullable':
        out += ': ' + stringifyType(ty);
        break;
      case 'quantity':
      case 'list':
      case 'null':
        throw new Error(
          `Type ${stringifyType(ty)} cannot be used as a type declaration`
        );
      default:
        assertNever(ty);
    }
  }
  out += ' = ';
  out += stringifyExpr(assignment.expr);
  return out;
}

export function stringifyAst(ast: ASTExpr | ASTStmt | ASTProgram): string {
  if (Array.isArray(ast)) {
    return stringifyProgram(ast);
  } else if ('t' in ast && ast.t === 'assign') {
    return stringifyStmt(ast);
  } else if ('t' in ast) {
    return stringifyExpr(ast);
  } else {
    throw new Error('Unknown AST type');
  }
}

// Return the "canonical match order" version of a call, in which the order of
// the matching (aka keyword) arguments has been normalized in the following
// way:
// - First, every exact match is written down in alphabetical order
// - Then, every fuzzy match is written down *in the order they originally
//   appeared*
//
// See https://www.notion.so/watershedclimate/LScript-matching-args-9ddb69ba61314dcdb4a7f83c4a1ac5e6
// for more on how we made this decision
export function canonicalMatchOrder(call: ASTCall): ASTCall {
  const exactKeys = new Array<string>();
  const fuzzyKeys = new Array<string>();
  for (const [arg, { t }] of call.kwargs) {
    switch (t) {
      case 'exact':
        exactKeys.push(arg);
        break;
      case 'fuzzy':
        fuzzyKeys.push(arg);
        break;
      default:
        assertNever(t);
    }
  }
  exactKeys.sort();

  const kwargs = new Map();
  for (const key of exactKeys) {
    kwargs.set(key, call.kwargs.get(key));
  }
  for (const key of fuzzyKeys) {
    kwargs.set(key, call.kwargs.get(key));
  }

  return {
    t: 'call',
    name: call.name,
    args: call.args,
    kwargs,
  };
}

// Returns true if two expressions are considered equal. Prefer using this to
// lodash's deep equality.
export function expressionsEqual(a: ASTExpr, b: ASTExpr): boolean {
  // I'd prefer to check a.t === b.t up here, but typescript doesn't propagate
  // that through the switch statement below. So to appease the typechecker the
  // code looks like this instead
  switch (a.t) {
    case 'string':
      if (b.t !== 'string') {
        return false;
      }
      return a.v === b.v;
    case 'number':
      if (b.t !== 'number') {
        return false;
      }
      return numbersApproximatelyEqual(a.v, b.v);
    case 'boolean':
      if (b.t !== 'boolean') {
        return false;
      }
      return a.v === b.v;
    case 'unary':
      if (b.t !== 'unary') {
        return false;
      }
      return a.op === b.op && expressionsEqual(a.expr, b.expr);
    case 'identifier':
      if (b.t !== 'identifier') {
        return false;
      }
      return a.name === b.name;
    case 'binary':
      if (b.t !== 'binary') {
        return false;
      }
      return (
        a.op === b.op &&
        expressionsEqual(a.l, b.l) &&
        expressionsEqual(a.r, b.r)
      );
    case 'comparison':
      if (b.t !== 'comparison') {
        return false;
      }
      return (
        a.op === b.op &&
        expressionsEqual(a.l, b.l) &&
        expressionsEqual(a.r, b.r)
      );
    case 'call':
      if (b.t !== 'call') {
        return false;
      }

      a = canonicalMatchOrder(a);
      b = canonicalMatchOrder(b);

      if (
        a.name !== b.name ||
        a.args.length !== b.args.length ||
        a.kwargs.size !== b.kwargs.size
      ) {
        return false;
      }
      for (let i = 0; i < a.args.length; i++) {
        if (!expressionsEqual(a.args[i], b.args[i])) {
          return false;
        }
      }

      // The correctness of this is dependent on us normalizing both a and b to
      // the "canonical match order" (see above)
      const aKwargs = Array.from(a.kwargs);
      const bKwargs = Array.from(b.kwargs);
      for (let i = 0; i < aKwargs.length; i++) {
        const [aName, { t: aT, expr: aExpr }] = aKwargs[i];
        const [bName, { t: bT, expr: bExpr }] = bKwargs[i];
        if (aName !== bName || aT !== bT || !expressionsEqual(aExpr, bExpr)) {
          return false;
        }
      }

      return true;
    case 'null':
      if (b.t !== 'null') {
        return false;
      }
      return true;
    default:
      assertNever(a);
  }
}
